import { Link } from 'react-router-dom';
import { Briefcase } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-blue-900" />
              <span className="text-xl font-bold text-blue-900">JobSuiza</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-8">Términos y Condiciones de Uso</h1>
        <p className="text-sm text-slate-600 mb-8">Última actualización: {new Date().toLocaleDateString('es-ES')}</p>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Aceptación de los Términos</h2>
            <p className="text-slate-700 leading-relaxed">
              Bienvenido a JobSuiza. Al acceder y utilizar este sitio web (jobsuiza.com), usted acepta estar vinculado 
              por estos Términos y Condiciones, nuestra Política de Privacidad y todas las leyes aplicables. 
              Si no está de acuerdo con alguno de estos términos, no utilice este sitio.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Información del Operador</h2>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="text-slate-700 mb-2"><strong>Operado por:</strong> Lidia Moyano Acuna</p>
              <p className="text-slate-700 mb-2"><strong>Dirección:</strong> Tägernstrasse 1, 8127 Forch, Suiza</p>
              <p className="text-slate-700 mb-2"><strong>Email de contacto:</strong> info@jobsuiza.com</p>
              <p className="text-slate-700"><strong>Sitio web:</strong> jobsuiza.com</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Descripción del Servicio</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              JobSuiza es una plataforma de búsqueda de empleo que:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Agrega ofertas de trabajo de múltiples fuentes (Adzuna API y otros)</li>
              <li>Permite a los usuarios buscar y filtrar empleos en Suiza</li>
              <li>Ofrece funciones de guardado de favoritos y alertas de empleo</li>
              <li>Redirige a los usuarios a las páginas de aplicación de los empleadores</li>
              <li>Proporciona información sobre requisitos de español para cada puesto</li>
              <li>Ofrece un servicio de newsletter gratuito con ofertas destacadas</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Importante:</strong> JobSuiza es un agregador de empleos. No somos empleadores ni agencia de contratación. 
              No controlamos el proceso de contratación ni las condiciones de empleo.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Registro de Cuenta</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3">4.1 Creación de Cuenta</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Para acceder a ciertas funciones (favoritos, alertas), debe crear una cuenta proporcionando:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-700 mb-4">
              <li>Nombre completo</li>
              <li>Dirección de correo electrónico válida</li>
              <li>Contraseña segura (o autenticación con Google)</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">4.2 Responsabilidad de la Cuenta</h3>
            <p className="text-slate-700 leading-relaxed">
              Usted es responsable de:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Mantener la confidencialidad de su contraseña</li>
              <li>Todas las actividades que ocurran bajo su cuenta</li>
              <li>Proporcionar información precisa y actualizada</li>
              <li>Notificarnos inmediatamente si sospecha uso no autorizado</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">4.3 Edad Mínima</h3>
            <p className="text-slate-700 leading-relaxed">
              Debe tener al menos 16 años para crear una cuenta en JobSuiza.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Uso Aceptable</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3">5.1 Usos Permitidos</h3>
            <p className="text-slate-700 leading-relaxed mb-2">Puede usar JobSuiza para:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-700 mb-4">
              <li>Buscar oportunidades de empleo en Suiza</li>
              <li>Guardar ofertas de interés</li>
              <li>Crear alertas de empleo personalizadas</li>
              <li>Acceder a información sobre requisitos de idioma español</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">5.2 Usos Prohibidos</h3>
            <p className="text-slate-700 leading-relaxed mb-2">Está prohibido:</p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Usar el sitio para cualquier propósito ilegal o no autorizado</li>
              <li>Realizar scraping automatizado o extracción masiva de datos</li>
              <li>Intentar acceder a áreas restringidas o cuentas de otros usuarios</li>
              <li>Transmitir virus, malware o código malicioso</li>
              <li>Suplantar a otra persona o entidad</li>
              <li>Interferir con el funcionamiento del sitio</li>
              <li>Usar bots, scrapers o herramientas automatizadas sin permiso explícito</li>
              <li>Republicar contenido de JobSuiza sin autorización</li>
              <li>Enviar spam o comunicaciones no solicitadas</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Ofertas de Empleo</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3">6.1 Fuentes de Datos</h3>
            <p className="text-slate-700 leading-relaxed">
              Las ofertas de empleo provienen de:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-700 mb-4">
              <li>Adzuna API (agregador de empleos de terceros)</li>
              <li>Publicaciones directas de empleadores (futuro)</li>
              <li>Otras fuentes externas que podamos agregar</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">6.2 Sin Garantías</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              JobSuiza no garantiza:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>La exactitud, actualidad o disponibilidad de las ofertas</li>
              <li>Que las ofertas cumplan con la legislación laboral</li>
              <li>La legitimidad de los empleadores o las ofertas</li>
              <li>Que obtendrá una entrevista o empleo</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">6.3 Detección de Nivel de Español</h3>
            <p className="text-slate-700 leading-relaxed">
              Usamos IA (GPT-5.1) para detectar automáticamente el nivel de español requerido. 
              Esta detección es una estimación y puede no ser 100% precisa. Siempre verifique los 
              requisitos en la oferta original del empleador.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">6.4 Redirección a Empleadores</h3>
            <p className="text-slate-700 leading-relaxed">
              Al hacer clic en "Ver Oferta" o "Aplicar", será redirigido al sitio web del empleador. 
              A partir de ese momento, está sujeto a sus términos y políticas de privacidad. 
              JobSuiza no es responsable del proceso de aplicación o contratación.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Propiedad Intelectual</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3">7.1 Contenido de JobSuiza</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              El diseño, logotipos, textos, gráficos y código de JobSuiza son propiedad de Lidia Moyano Acuna 
              y están protegidos por leyes de propiedad intelectual.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">7.2 Contenido de Terceros</h3>
            <p className="text-slate-700 leading-relaxed">
              Las ofertas de empleo y sus descripciones pertenecen a sus respectivos empleadores. 
              Datos de Adzuna están sujetos a sus términos de uso.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">7.3 Uso Limitado</h3>
            <p className="text-slate-700 leading-relaxed">
              Se le concede una licencia limitada, no exclusiva, no transferible para usar el sitio 
              únicamente para su uso personal no comercial.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Privacidad y Protección de Datos</h2>
            <p className="text-slate-700 leading-relaxed">
              Su privacidad es importante para nosotros. Consulte nuestra{' '}
              <Link to="/privacy-policy" className="text-blue-900 underline hover:text-blue-700">
                Política de Privacidad
              </Link>
              {' '}para entender cómo recopilamos, usamos y protegemos sus datos personales.
            </p>
            <p className="text-slate-700 leading-relaxed mt-3">
              Al usar JobSuiza, consiente el procesamiento de sus datos según nuestra Política de Privacidad.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Publicidad</h2>
            <p className="text-slate-700 leading-relaxed">
              JobSuiza muestra publicidad a través de Google AdSense para financiar el servicio gratuito. 
              Los anuncios pueden ser personalizados según su actividad de navegación. Consulte nuestra 
              Política de Cookies para más información sobre cómo gestionar preferencias de publicidad.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Enlaces a Sitios de Terceros</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              JobSuiza contiene enlaces a sitios web de terceros (empleadores, Adzuna, etc.). 
              No controlamos ni somos responsables del contenido, políticas de privacidad o prácticas 
              de estos sitios externos.
            </p>
            <p className="text-slate-700 leading-relaxed">
              El acceso a sitios de terceros es bajo su propio riesgo. Le recomendamos revisar sus 
              términos y políticas antes de proporcionar información personal.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Limitación de Responsabilidad</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3">11.1 Servicio "Tal Cual"</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              JobSuiza se proporciona "tal cual" y "según disponibilidad", sin garantías de ningún tipo, 
              expresas o implícitas, incluyendo pero no limitado a:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-700 mb-4">
              <li>Disponibilidad ininterrumpida o libre de errores</li>
              <li>Exactitud o fiabilidad del contenido</li>
              <li>Adecuación para un propósito particular</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">11.2 Exclusión de Responsabilidad</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              En la medida máxima permitida por la ley, JobSuiza y su operador no serán responsables de:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700">
              <li>Daños directos, indirectos, incidentales, especiales o consecuentes</li>
              <li>Pérdida de beneficios, datos, uso o buena voluntad</li>
              <li>Ofertas de empleo fraudulentas o empleadores no confiables</li>
              <li>Decisiones tomadas basándose en información del sitio</li>
              <li>Interrupciones del servicio o errores técnicos</li>
              <li>Acciones u omisiones de terceros (empleadores, Adzuna, etc.)</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">11.3 Responsabilidad Máxima</h3>
            <p className="text-slate-700 leading-relaxed">
              Si se determina que JobSuiza es responsable de algún daño, nuestra responsabilidad máxima 
              será limitada a 100 CHF o el monto que usted nos haya pagado (si aplica), lo que sea menor.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Indemnización</h2>
            <p className="text-slate-700 leading-relaxed">
              Usted acepta indemnizar, defender y mantener indemne a JobSuiza y su operador de cualquier 
              reclamo, daño, obligación, pérdida, responsabilidad, costo o deuda que surja de:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mt-3">
              <li>Su uso del sitio</li>
              <li>Violación de estos Términos</li>
              <li>Violación de derechos de terceros</li>
              <li>Cualquier contenido que publique (si aplica en el futuro)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Modificaciones del Servicio</h2>
            <p className="text-slate-700 leading-relaxed">
              Nos reservamos el derecho de:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mt-3">
              <li>Modificar o discontinuar el servicio (temporal o permanentemente)</li>
              <li>Cambiar características, funciones o contenido</li>
              <li>Suspender o terminar cuentas por violación de términos</li>
              <li>Limitar acceso a ciertas funciones</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-3">
              No seremos responsables ante usted o terceros por modificaciones o discontinuación del servicio.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Terminación</h2>
            
            <h3 className="text-xl font-semibold text-slate-900 mb-3">14.1 Por su Parte</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Puede terminar su cuenta en cualquier momento enviando un email a info@jobsuiza.com 
              solicitando la eliminación de su cuenta.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">14.2 Por Nuestra Parte</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Podemos suspender o terminar su cuenta si:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-700">
              <li>Viola estos Términos y Condiciones</li>
              <li>Usa el sitio de manera fraudulenta o ilegal</li>
              <li>Por cualquier razón, con o sin causa, a nuestra discreción</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mb-3">14.3 Efectos de la Terminación</h3>
            <p className="text-slate-700 leading-relaxed">
              Tras la terminación, perderá acceso a su cuenta y todos los datos asociados 
              (favoritos, alertas, preferencias).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Modificaciones de los Términos</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Nos reservamos el derecho de modificar estos Términos en cualquier momento. 
              Los cambios materiales serán notificados mediante:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-700 mb-4">
              <li>Un aviso en la página de inicio</li>
              <li>Email a usuarios registrados</li>
              <li>Actualización de la fecha de "Última actualización"</li>
            </ul>
            <p className="text-slate-700 leading-relaxed">
              El uso continuado del sitio después de los cambios constituye aceptación de los nuevos términos.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">16. Ley Aplicable y Jurisdicción</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Estos Términos y Condiciones se rigen por las leyes de Suiza, sin referencia a 
              sus principios de conflicto de leyes.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Cualquier disputa relacionada con estos términos estará sujeta a la jurisdicción 
              exclusiva de los tribunales del cantón de Zürich, Suiza.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">17. Resolución de Disputas</h2>
            <p className="text-slate-700 leading-relaxed">
              En caso de disputa, le animamos a contactarnos primero en info@jobsuiza.com 
              para intentar resolver el problema amistosamente antes de iniciar acciones legales.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">18. Divisibilidad</h2>
            <p className="text-slate-700 leading-relaxed">
              Si alguna disposición de estos Términos se considera inválida o inaplicable, 
              las disposiciones restantes permanecerán en pleno vigor y efecto.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">19. Acuerdo Completo</h2>
            <p className="text-slate-700 leading-relaxed">
              Estos Términos y Condiciones, junto con nuestra Política de Privacidad y Política de Cookies, 
              constituyen el acuerdo completo entre usted y JobSuiza respecto al uso del sitio.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">20. Contacto</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Para preguntas sobre estos Términos y Condiciones, contáctenos:
            </p>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-slate-700 mb-2"><strong>Email:</strong> info@jobsuiza.com</p>
              <p className="text-slate-700">
                <strong>Dirección:</strong> Lidia Moyano Acuna, Tägernstrasse 1, 8127 Forch, Suiza
              </p>
            </div>
          </section>

          <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6 mt-8">
            <p className="text-slate-700 leading-relaxed">
              <strong>Al utilizar JobSuiza, usted reconoce que ha leído, entendido y acepta estar vinculado 
              por estos Términos y Condiciones.</strong>
            </p>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-200">
          <Link to="/" className="text-blue-900 hover:text-blue-700 underline">
            ← Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
